/**
 * Minimal markdown renderer for method bodies. Its own module, not part of components.tsx, because
 * it is pure presentation with no i18n or router dependency — which is what lets it be unit-tested
 * without a DOM (components.tsx pulls in i18n, which reads `location` at import time).
 */
import * as React from "react";

/**
 * Supports: `## headings`, **bold**, blank-line paragraphs, numbered and bulleted lists.
 *
 * Headings and bullets are here because the method bodies use them. A heading normally shares its
 * block with the list under it (`## Materiais\n- Chat…`), so a block is peeled one heading at a
 * time and the remainder re-classified — checking only the first line would render every list that
 * follows a heading as literal `- text`.
 */
export function Markdown({ md }: { md: string }): React.ReactElement {
  const blocks = md.split(/\n\s*\n/).filter((b) => b.trim() !== "");
  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: string }): React.ReactElement {
  // trim() first: an authored body ending in a newline leaves a trailing "" line, and the
  // `every(...)` list tests below reject it — which would silently render a whole list as literal
  // "1. a<br>2. b" text. Blocks are split on blank lines, so they never contain interior blanks.
  const lines = block.trim().split("\n");
  const [first = "", ...rest] = lines;

  if (/^#{2,3}\s+/.test(first)) {
    return (
      <>
        <h2 className="font-display text-display-sm">{inline(first.replace(/^#{2,3}\s+/, ""))}</h2>
        {rest.length > 0 && <Block block={rest.join("\n")} />}
      </>
    );
  }

  if (lines.every((l) => /^\d+\.\s/.test(l.trim()))) {
    return (
      <ol className="ml-5 list-decimal space-y-1">
        {lines.map((l, j) => (
          <li key={j}>{inline(l.trim().replace(/^\d+\.\s/, ""))}</li>
        ))}
      </ol>
    );
  }

  if (lines.every((l) => /^[-*]\s/.test(l.trim()))) {
    return (
      <ul className="ml-5 list-disc space-y-1">
        {lines.map((l, j) => (
          <li key={j}>{inline(l.trim().replace(/^[-*]\s/, ""))}</li>
        ))}
      </ul>
    );
  }

  return (
    <p className="max-w-prose whitespace-pre-wrap leading-normal">
      {lines.map((l, j) => (
        <React.Fragment key={j}>
          {inline(l)}
          {j < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  );
}

function inline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>,
  );
}
